/**
 * Contratto fra il builder e il modello AI. Unica fonte: il catalogo (emailBlocks.js).
 *
 *  - buildVocabulary()          catalogo in forma compatta per il system prompt
 *  - documentView(blocks)       lo stato del template come lo vede il modello:
 *                               id corti (b1, b2…), solo settings (o content per il testo libero)
 *  - applyActions(...)          interprete puro delle operazioni emesse dal modello
 *  - historyContent(msg)        turno della chat per la cronologia, con l'esito delle operazioni
 *
 * Le operazioni sono quattro: insert, update, remove, move. Il modello non scrive mai HTML
 * di layout: sceglie blocchi del catalogo e ne compila i settings; l'HTML lo produce il renderer.
 * L'unico HTML ammesso e' il testo ricco dei blocchi editabili, ripulito da sanitizeRichText.
 *
 * Il formato della risposta e' chiesto nel prompt (backend), non imposto con l'output strutturato
 * dell'API: lo schema delle operazioni supera il limite della grammatica ("schema too complex").
 * La validazione per singola operazione qui sotto e' quindi l'unica garanzia.
 */
import { blockCategories, findBlockDef, createBlock, renderBlockContent, hasTitleScale, imageGenSpec, imageRatios, fitToColumns } from './emailBlocks'

// Solo proprieta' che il renderer emette davvero: una chiave accettata e mai resa e' uno
// scarto silenzioso, lo stesso difetto che rendeva i titoli non dimensionabili.
// Il bordo vale solo per il blocco (styles), nella forma che scrive il pannello: sul titolo nessuno
// lo legge. border-radius e max-width restano fuori: le tabelle della mail hanno border-collapse,
// che il raggio lo ignora, e il motore di Word (Outlook per Windows) pure.
var TEXT_STYLE_KEYS = ['padding', 'background-color', 'color', 'font-family', 'font-size', 'text-align', 'line-height',
	'font-weight', 'letter-spacing', 'text-transform']
var BLOCK_STYLE_KEYS = TEXT_STYLE_KEYS.concat(['border-width', 'border-style', 'border-color', 'border-radius'])
var BORDER_STYLES = ['solid', 'dashed', 'dotted', 'none']

// I valori finiscono dentro un attributo style="..." costruito per concatenazione. Vietati i
// caratteri che ne escono o che aprono una funzione CSS (url(), expression()); l'apice singolo
// resta ammesso perche' i font stack lo usano ("'Helvetica Neue', Arial"), e le virgolette
// doppie diventano apici invece di far scartare l'operazione.
var STYLE_VALUE_FORBIDDEN = /[<>()\\]|;\s*\S/
var ALLOWED_TAGS = ['p', 'h1', 'h2', 'h3', 'strong', 'b', 'em', 'i', 'u', 'a', 'br', 'ul', 'ol', 'li', 'span']
// data-tracking-notice marca il link al tracciamento: il backend lo toglie se il tenant lo spegne
var ALLOWED_ATTRS = { a: ['href', 'target', 'style', 'data-tracking-notice'], p: ['style'], h1: ['style'], h2: ['style'], h3: ['style'], span: ['style'], li: ['style'], ul: ['style'], ol: ['style'] }

// --- Vocabolario ---

function fieldSpec(f) {
	var spec = { key: f.key, type: f.type, label: f.label }
	if (f.options) spec.options = f.options.map(function(o) { return typeof o === 'object' ? o.value : o })
	if (f.type === 'align') spec.options = ['left', 'center', 'right']
	if (f.type === 'list') {
		spec.item = f.fields.map(fieldSpec)
		if (f.max) spec.max = f.max
	}
	return spec
}

export function buildVocabulary() {
	var out = []
	for (var c = 0; c < blockCategories.length; c++) {
		var cat = blockCategories[c]
		for (var i = 0; i < cat.blocks.length; i++) {
			var def = cat.blocks[i]
			if (def.hidden) continue
			var entry = { type: def.type, category: cat.name, label: def.label, description: def.description || '' }
			if (def.isLayout) {
				entry.columns = def.defaults().columns.map(function(col) { return col.width })
			} else if (def.fields) {
				entry.fields = def.fields.filter(function(f) { return f.key }).map(fieldSpec)
				if (hasTitleScale(def)) entry.titleScale = true
				if (imageGenSpec(def.type)) entry.canGenerateImage = true
			} else {
				entry.content = 'HTML semplice: ' + ALLOWED_TAGS.join(', ')
			}
			out.push(entry)
		}
	}
	return out
}

// --- Vista del documento ---

export function documentView(blocks) {
	var aliasToId = {}
	var idToAlias = {}
	var n = 0

	function alias(id) {
		if (!idToAlias[id]) {
			n++
			idToAlias[id] = 'b' + n
			aliasToId['b' + n] = id
		}
		return idToAlias[id]
	}

	function view(b) {
		var def = findBlockDef(b.type)
		var v = { id: alias(b.id), type: b.type }
		if (b.columns) {
			v.columns = b.columns.map(function(col) { return { width: col.width, children: col.children.map(view) } })
		} else if (def && def.render) {
			v.settings = b.settings
		} else {
			v.content = b.content
		}
		if (b.styles && Object.keys(b.styles).length) v.styles = b.styles
		if (b.titleStyles && Object.keys(b.titleStyles).length) v.titleStyles = b.titleStyles
		return v
	}

	return { doc: blocks.map(view), aliasToId: aliasToId }
}

// --- Sanitizzazione del testo libero ---

/**
 * Input:  html emesso dal modello per un blocco editabile
 * Fa:     tiene solo i tag della lista chiusa (gli altri vengono "sbucciati" lasciando il testo),
 *         toglie attributi non ammessi e href non http/mailto/variabile
 * Output: html pulito (in ambienti senza DOM torna invariato)
 */
export function sanitizeRichText(html) {
	if (typeof document === 'undefined') return html
	var root = document.createElement('div')
	root.innerHTML = html || ''
	cleanNode(root)
	return root.innerHTML
}

function cleanNode(node) {
	var children = Array.prototype.slice.call(node.childNodes)
	for (var i = 0; i < children.length; i++) {
		var child = children[i]
		if (child.nodeType === 3) continue
		if (child.nodeType !== 1) { node.removeChild(child); continue }
		var tag = child.tagName.toLowerCase()
		if (tag === 'script' || tag === 'style' || tag === 'iframe') { node.removeChild(child); continue }
		if (ALLOWED_TAGS.indexOf(tag) === -1) {
			cleanNode(child)
			while (child.firstChild) node.insertBefore(child.firstChild, child)
			node.removeChild(child)
			continue
		}
		var attrs = Array.prototype.slice.call(child.attributes)
		for (var a = 0; a < attrs.length; a++) {
			if ((ALLOWED_ATTRS[tag] || []).indexOf(attrs[a].name) === -1) child.removeAttribute(attrs[a].name)
		}
		if (tag === 'a') {
			var href = child.getAttribute('href') || ''
			if (!/^(https?:|mailto:|\{\{)/i.test(href)) child.removeAttribute('href')
			child.setAttribute('target', '_blank')
		}
		cleanNode(child)
	}
}

// --- Validazione settings/styles contro lo schema del blocco ---

function fail(msg) { throw new Error(msg) }

function validateValue(f, value, path) {
	if (value == null) return undefined
	switch (f.type) {
		case 'select':
			var allowed = f.options.map(function(o) { return typeof o === 'object' ? o.value : o })
			if (allowed.indexOf(String(value)) === -1) fail(path + ': valore "' + value + '" non tra ' + allowed.join('/'))
			return String(value)
		case 'align':
			if (['left', 'center', 'right'].indexOf(value) === -1) fail(path + ': allineamento "' + value + '" non valido')
			return value
		case 'number':
			if (isNaN(parseFloat(value))) fail(path + ': "' + value + '" non e\' un numero')
			return String(parseFloat(value))
		case 'list':
			if (!Array.isArray(value)) fail(path + ': deve essere una lista')
			if (f.max && value.length > f.max) fail(path + ': massimo ' + f.max + ' elementi')
			return value.map(function(item, i) {
				var base = f.newItem ? f.newItem() : {}
				return Object.assign(base, validateSettings(f.fields, item, path + '[' + i + ']'))
			})
		default:
			if (typeof value !== 'string' && typeof value !== 'number') fail(path + ': atteso testo')
			return String(value)
	}
}

function validateSettings(fields, settings, path) {
	if (!settings || typeof settings !== 'object') fail(path + ': settings deve essere un oggetto')
	var byKey = {}
	for (var i = 0; i < fields.length; i++) if (fields[i].key) byKey[fields[i].key] = fields[i]
	var out = {}
	for (var key in settings) {
		if (!byKey[key]) fail(path + ': campo sconosciuto "' + key + '"')
		var v = validateValue(byKey[key], settings[key], path + '.' + key)
		if (v !== undefined) out[key] = v
	}
	return out
}

// Una chiave di stile sbagliata (sconosciuta o con un valore non ammesso) si toglie e si annota
// senza scartare il blocco: perdere un blocco intero, e con lui quelli agganciati dopo, per un
// dettaglio tipografico e' un danno molto piu' grande della chiave ignorata
function validateStyles(styles, label, notes) {
	if (!styles || typeof styles !== 'object') {
		note(notes, label + ' ignorato: deve essere un oggetto')
		return {}
	}
	var keys = label === 'titleStyles' ? TEXT_STYLE_KEYS : BLOCK_STYLE_KEYS
	styles = expandBorder(styles, keys)
	var out = {}
	for (var key in styles) {
		if (keys.indexOf(key) === -1) {
			note(notes, label + ': style sconosciuto "' + key + '" ignorato')
			continue
		}
		if (styles[key] == null) continue
		var value = String(styles[key]).trim().replace(/"/g, "'")
		if (STYLE_VALUE_FORBIDDEN.test(value)) {
			note(notes, label + ': valore non ammesso per "' + key + '" ignorato')
			continue
		}
		if (key === 'border-style' && BORDER_STYLES.indexOf(value) === -1) {
			note(notes, label + ': border-style "' + value + '" non valido, ignorato')
			continue
		}
		if (value) out[key] = value
	}
	return out
}

// Il modello scrive quasi sempre il bordo abbreviato ("1px solid #ccc"): si scompone nella forma
// che usano pannello e renderer invece di scartarlo come chiave sconosciuta
function expandBorder(styles, keys) {
	if (styles.border == null || keys.indexOf('border-width') === -1) return styles
	var out = Object.assign({}, styles)
	var parts = String(styles.border).trim().split(/\s+/)
	delete out.border
	for (var i = 0; i < parts.length; i++) {
		if (/^\d+(px)?$/.test(parts[i])) out['border-width'] = parseInt(parts[i], 10) + 'px'
		else if (BORDER_STYLES.indexOf(parts[i]) !== -1) out['border-style'] = parts[i]
		else out['border-color'] = parts[i]
	}
	return out
}

function note(notes, message) {
	if (notes) notes.push(message)
}

/**
 * Input:  blocco costruito, valore di `generateImage` emesso dal modello (true o proporzione),
 *         coda a cui accodare
 * Fa:     mette in coda la generazione della foto se il blocco la ammette e ha un prompt.
 *         Una proporzione non valida NON fa scartare l'operazione: si ripiega su quella di
 *         partenza del blocco, perche' perdere il layout per un "21:9" sarebbe un danno
 *         molto piu' grande del formato sbagliato
 * Output: niente; accoda su `pending`
 */
function queueImage(block, wanted, pending) {
	if (!pending || !wanted) return

	var spec = imageGenSpec(block.type)
	if (!spec) return

	var prompt = String((block.settings || {}).imagePrompt || '').trim()
	if (!prompt) return

	var ratio = spec.ratio
	for (var i = 0; i < imageRatios.length; i++) {
		if (imageRatios[i].value === wanted) ratio = wanted
	}

	pending.push({ blockId: block.id, srcKey: spec.srcKey, prompt: prompt, ratio: ratio })
}

/**
 * Input:  spec { type, id?, settings?, content?, styles?, columns?, generateImage? } dal modello
 * Fa:     crea il blocco dal catalogo, applica settings/content/styles validati, figli ricorsivi
 * Output: blocco pronto per il canvas
 */
function buildBlock(spec, map, pending, notes) {
	if (!spec || !spec.type) fail('blocco senza type')
	var def = findBlockDef(spec.type)
	if (!def || def.hidden) fail('tipo di blocco sconosciuto "' + spec.type + '"')
	var block = createBlock(spec.type)

	if (def.isLayout) {
		if (spec.columns) {
			if (!Array.isArray(spec.columns) || spec.columns.length > block.columns.length) fail(spec.type + ': colonne non coerenti')
			for (var c = 0; c < spec.columns.length; c++) {
				var kids = (spec.columns[c] && spec.columns[c].children) || []
				block.columns[c].children = kids.map(function(k) { return buildBlock(k, map, pending, notes) })
			}
		}
	} else if (def.render) {
		// I default del catalogo sono segnaposto pensati per chi trascina il blocco a mano
		// ("Offerta speciale", "Via Esempio 1"). Su un blocco creato dal modello un testo che
		// lui non ha scritto e' testo finto che finisce in una mail vera: resta vuoto, e il
		// renderer salta il campo. Gli altri default (colori, larghezze) restano.
		var fields = def.fields || []
		for (var f = 0; f < fields.length; f++) {
			var key = fields[f].key
			if (!key || (fields[f].type !== 'text' && fields[f].type !== 'textarea')) continue
			if (!spec.settings || spec.settings[key] == null) block.settings[key] = ''
		}
		if (spec.settings) Object.assign(block.settings, validateSettings(fields, spec.settings, spec.type))
	} else if (spec.content != null) {
		block.content = sanitizeRichText(String(spec.content))
	}

	if (spec.styles) Object.assign(block.styles, validateStyles(spec.styles, 'styles', notes))
	if (spec.titleStyles) {
		if (block.titleStyles) Object.assign(block.titleStyles, validateStyles(spec.titleStyles, 'titleStyles', notes))
		else note(notes, spec.type + ': non ha un titolo proprio, titleStyles ignorato')
	}
	queueImage(block, spec.generateImage, pending)
	block.content = renderBlockContent(block)
	if (spec.id) map[spec.id] = block.id
	return block
}

// --- Navigazione dell'albero ---

function locate(blocks, id) {
	for (var i = 0; i < blocks.length; i++) {
		if (blocks[i].id === id) return { list: blocks, index: i, block: blocks[i] }
		if (blocks[i].columns) {
			for (var c = 0; c < blocks[i].columns.length; c++) {
				var found = locate(blocks[i].columns[c].children, id)
				if (found) return found
			}
		}
	}
	return null
}

function resolve(blocks, map, ref) {
	if (!ref) fail('id mancante')
	var id = map[ref] || ref
	var loc = locate(blocks, id)
	if (!loc) fail('blocco "' + ref + '" non trovato')
	return loc
}

function closingIndex(blocks) {
	for (var i = 0; i < blocks.length; i++) {
		var def = findBlockDef(blocks[i].type)
		if (def && def.closing) return i
	}
	return -1
}

/**
 * Input:  documento, alias, blocco, position del modello, notes, strict (move)
 * Fa:     mette il blocco al suo posto. Una posizione che non si trova (blocco di riferimento
 *         scartato, id sbagliato, contenitore che non c'e') manda il blocco in fondo invece di
 *         scartarlo: il modello scrive gli insert in ordine di lettura agganciandoli al precedente,
 *         quindi in fondo la sequenza si ricompone invece di cadere a catena. Con strict (move)
 *         resta un errore: spostare in fondo un blocco che stava altrove sarebbe peggio.
 *         Al primo livello niente finisce dopo il footer: il modello a volte accoda blocchi
 *         senza posizione dopo averlo gia' inserito, e in una mail vera starebbero sotto la
 *         disiscrizione. Vanno subito prima, con una nota
 */
function insertAt(blocks, map, block, position, notes, strict) {
	position = position || {}
	var top = function(index, reason) {
		var def = findBlockDef(block.type)
		var closing = def && def.closing ? -1 : closingIndex(blocks)
		var moved = closing !== -1 && index > closing
		if (reason) note(notes, reason + (moved ? ': messo in fondo, prima del footer' : ': messo in fondo'))
		else if (moved) note(notes, 'dopo il footer non ci va niente: messo prima del footer')
		blocks.splice(moved ? closing : index, 0, block)
	}
	var missing = function(reason) {
		if (strict) fail(reason)
		top(blocks.length, reason)
	}
	if (position.into) {
		var target = locate(blocks, map[position.into] || position.into)
		if (!target) return missing('blocco "' + position.into + '" non trovato')
		if (!target.block.columns) return missing('"' + position.into + '" non e\' un contenitore a colonne')
		var col = target.block.columns[position.column || 0]
		if (!col) return missing('colonna ' + position.column + ' inesistente')
		if (block.columns) return missing('un contenitore non puo\' stare dentro una colonna')
		col.children.splice(position.index != null ? position.index : col.children.length, 0, block)
		return
	}
	if (position.after) {
		var ref = locate(blocks, map[position.after] || position.after)
		if (!ref) return missing('blocco "' + position.after + '" non trovato')
		if (ref.list === blocks) return top(ref.index + 1)
		ref.list.splice(ref.index + 1, 0, block)
		return
	}
	top(position.index != null ? position.index : blocks.length)
}

function applyOne(blocks, action, map, pending, notes) {
	switch (action.op) {
		case 'insert': {
			// In una insert styles e titleStyles vanno dentro `block`, ma il modello a volte li
			// mette al primo livello com'e' giusto nelle update. Scartarli vorrebbe dire far
			// uscire il blocco senza colori ne' spaziature, e in silenzio: si recuperano.
			var spec = action.block
			if (spec && typeof spec === 'object') {
				if (action.styles && !spec.styles) spec = Object.assign({}, spec, { styles: action.styles })
				if (action.titleStyles && !spec.titleStyles) spec = Object.assign({}, spec, { titleStyles: action.titleStyles })
				if (action.generateImage && !spec.generateImage) spec = Object.assign({}, spec, { generateImage: action.generateImage })
			}
			insertAt(blocks, map, buildBlock(spec, map, pending, notes), action.position, notes)
			return
		}
		case 'update': {
			var loc = resolve(blocks, map, action.id)
			var def = findBlockDef(loc.block.type)
			if (action.settings) {
				if (!def || !def.render) fail('"' + action.id + '" non ha settings')
				Object.assign(loc.block.settings, validateSettings(def.fields || [], action.settings, loc.block.type))
			}
			if (action.content != null) {
				if (!loc.block.editable) fail('"' + action.id + '" non e\' un blocco di testo libero')
				loc.block.content = sanitizeRichText(String(action.content))
			}
			if (action.styles) Object.assign(loc.block.styles, validateStyles(action.styles, 'styles', notes))
			if (action.titleStyles) {
				if (loc.block.titleStyles) Object.assign(loc.block.titleStyles, validateStyles(action.titleStyles, 'titleStyles', notes))
				else note(notes, '"' + action.id + '" non ha un titolo proprio, titleStyles ignorato')
			}
			queueImage(loc.block, action.generateImage, pending)
			loc.block.content = renderBlockContent(loc.block)
			return
		}
		case 'remove': {
			var r = resolve(blocks, map, action.id)
			r.list.splice(r.index, 1)
			return
		}
		case 'move': {
			var m = resolve(blocks, map, action.id)
			var moved = m.list.splice(m.index, 1)[0]
			try {
				insertAt(blocks, map, moved, action.position, notes, true)
			} catch (e) {
				m.list.splice(m.index, 0, moved)
				throw e
			}
			return
		}
		default:
			fail('operazione sconosciuta "' + action.op + '"')
	}
}

/**
 * Input:  blocks (stato corrente), actions (dal modello), aliasToId (dalla documentView inviata)
 * Fa:     applica le operazioni una per una su una copia; quelle non valide vengono scartate
 *         singolarmente con il motivo, le altre passano. Le correzioni fatte per non scartare
 *         (chiave di stile tolta, blocco messo in fondo) finiscono in `notes`
 * Output: { blocks, applied, rejected: [{ index, op, reason }], notes: [{ index, op, reason }],
 *           images: [{ blockId, srcKey, prompt, ratio }] }
 *         `images` e' l'elenco delle foto che il modello ha chiesto di generare: qui restano
 *         una lista: chi chiama decide se e quante pagarne
 */
export function applyActions(blocks, actions, aliasToId) {
	var next = JSON.parse(JSON.stringify(blocks))
	var map = Object.assign({}, aliasToId || {})
	var result = { blocks: next, applied: 0, rejected: [], notes: [], images: [] }
	var list = Array.isArray(actions) ? actions : []
	for (var i = 0; i < list.length; i++) {
		var op = list[i] && list[i].op
		var notes = []
		try {
			applyOne(next, list[i] || {}, map, result.images, notes)
			result.applied++
			for (var n = 0; n < notes.length; n++) result.notes.push({ index: i, op: op, reason: notes[n] })
		} catch (e) {
			result.rejected.push({ index: i, op: op, reason: e.message })
		}
	}
	fitToColumns(next)
	return result
}

// Tetto del backend su ogni turno della cronologia (history.*.content)
var HISTORY_MAX = 4000

/**
 * Input:  messaggio della chat ({ role, content, rejected?, notes? })
 * Fa:     testo del turno per la cronologia che va al modello. Per l'assistente aggiunge cosa il
 *         builder ha scartato o corretto: il modello scrive il messaggio prima che le operazioni
 *         vengano applicate, e senza l'esito il turno dopo (la seconda passata) crede che la mail
 *         sia quella che aveva descritto e ripete lo stesso errore
 * Output: stringa entro HISTORY_MAX, con l'esito sempre in coda
 */
export function historyContent(msg) {
	var content = String((msg && msg.content) || '')
	var assistant = !!msg && msg.role === 'assistant'
	var lines = ((assistant && msg.rejected) || []).map(function(r) {
		return '- operazione ' + (r.index + 1) + ' (' + r.op + ') SCARTATA, non e\' nel documento: ' + r.reason
	}).concat(((assistant && msg.notes) || []).map(function(n) {
		return '- operazione ' + (n.index + 1) + ' (' + n.op + ') applicata con una correzione: ' + n.reason
	}))
	if (!lines.length) return content.slice(0, HISTORY_MAX)

	var tail = '\n\nESITO NEL BUILDER:\n' + lines.join('\n')
	return (content.slice(0, Math.max(0, HISTORY_MAX - tail.length)) + tail).slice(0, HISTORY_MAX)
}
